import React, { useState } from 'react';
import PortalModal from '../../Common/Modal/PortalModal';
import { XMarkIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { Editor } from '@tinymce/tinymce-react';
import BBCodeEditor from '../../Common/TextEditor/BBCodeEditor';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';

const SendMailConfirmationModal = React.forwardRef(({ initialEmail, onSendMail, hideModal }, ref) => {
    const [email, setEmail] = useState(initialEmail || '');
    const [isConfirming, setIsConfirming] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const { user } = useAuth()
    const userSignature = user?.signature ? user.signature : ''

    // Template HTML complet pour l'éditeur avec placeholder pour le lien
    const getInitialTemplate = () => {
        const signature = userSignature ? `<p>${userSignature}</p>` : '';
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Résultats de votre bilan de sensibilité</title>
            </head>
            <body style="margin:0;padding:0;background-color:#f8fafc;font-family:Arial,sans-serif;">
                <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#f8fafc;">
                    <tr>
                        <td align="center" style="padding:40px 20px;">
                            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:600px;background-color:#ffffff;border-radius:12px;box-shadow:0 4px 6px rgba(0,0,0,0.1);">
                                <!-- Header -->
                                <tr>
                                    <td style="padding:40px 40px 20px 40px;text-align:center;background:linear-gradient(135deg,#37cdbe 0%,#2bb3a6 100%);border-radius:12px 12px 0 0;">
                                        <h1 style="margin:0;color:#ffffff;font-size:28px;font-weight:700;line-height:1.2;">🎯 Votre Bilan de Sensibilité</h1>
                                        <p style="margin:10px 0 0 0;color:#ffffff;font-size:16px;opacity:0.9;">Résultats personnalisés prêts à consulter</p>
                                    </td>
                                </tr>
                                
                                <!-- Content -->
                                <tr>
                                    <td style="padding:40px;">
                                        <p style="margin:0 0 20px 0;color:#374151;font-size:16px;line-height:1.6;">Bonjour,</p>
                                        
                                        <p style="margin:0 0 30px 0;color:#374151;font-size:16px;line-height:1.6;">
                                            Nous avons le plaisir de vous informer que votre <strong>bilan de sensibilité personnalisé</strong> est maintenant disponible !
                                        </p>
                                        
                                        <!-- CTA Button -->
                                        <table cellpadding="0" cellspacing="0" border="0" style="margin:30px 0;">
                                            <tr>
                                                <td align="center">
                                                    <table cellpadding="0" cellspacing="0" border="0">
                                                        <tr>
                                                            <td style="background-color:#37cdbe;border-radius:8px;text-align:center;padding:0;">
                                                                <a href="{{CAPSULE_REVIEW_LINK}}" style="display:block;color:#ffffff !important;text-decoration:none !important;font-weight:bold !important;font-size:20px !important;font-family:Arial,sans-serif !important;border:none !important;outline:none !important;padding:12px 24px;width:100%;box-sizing:border-box;">📊 Consulter mon bilan</a>
                                                            </td>
                                                        </tr>
                                                    </table>
                                                </td>
                                            </tr>
                                        </table>
                                        
                                        <div style="background-color:#f1f5f9;border-left:4px solid #37cdbe;padding:20px;margin:30px 0;border-radius:0 8px 8px 0;">
                                            <p style="margin:0 0 10px 0;color:#1e293b;font-size:14px;font-weight:600;">💡 Que trouverez-vous dans ce bilan ?</p>
                                            <ul style="margin:0;padding-left:20px;color:#475569;font-size:14px;line-height:1.5;">
                                                <li>Analyse détaillée de vos sensibilités</li>
                                                <li>Résultats visuels et faciles à comprendre</li>
                                            </ul>
                                        </div>
                                        
                                        <p style="margin:30px 0 0 0;color:#6b7280;font-size:14px;line-height:1.6;">
                                            <strong>Note importante :</strong> Ce lien est personnel et sécurisé. Il restera actif pendant 1 an pour vous permettre de consulter vos résultats à tout moment.
                                        </p>

                                        ${signature}
                                    
                                    </td>
                                </tr>
                                
                                <!-- Footer -->
                                <tr>
                                    <td style="padding:20px 40px 40px 40px;text-align:center;background-color:#f8fafc;border-radius:0 0 12px 12px;">
                                        <p style="margin:0 0 10px 0;color:#6b7280;font-size:12px;line-height:1.4;">
                                            Si vous avez des questions, n'hésitez pas à nous contacter.
                                        </p>
                                        <p style="margin:0;color:#9ca3af;font-size:11px;">
                                            © 2025 Verbatim - Tous droits réservés
                                        </p>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                </table>
            </body>
            </html>
        `;
    };

    // Initialiser customHtml avec le template par défaut
    const [customHtml, setCustomHtml] = useState(() => getInitialTemplate());

    const handleEmailChange = (e) => {
        setEmail(e.target.value.toLowerCase().trim());
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Utiliser le contenu actuel ou le template par défaut si customHtml est vide
        const contentToCheck = customHtml || getInitialTemplate();

        // Vérifier si le placeholder du lien est présent (soit HTML avec placeholder, soit [LIEN_BILAN])
        if (!contentToCheck.includes('{{CAPSULE_REVIEW_LINK}}') && !contentToCheck.includes('[LIEN_BILAN]')) {
            toast.warning('Attention : Le lien "Accéder à mon bilan" a été supprimé.');
            toast.info('Veuillez ajouter le bouton pour que le destinataire puisse accéder à son bilan.', { delay: 1000 });
            return;
        }

        setIsConfirming(true);
    };

    const handleConfirmSend = async () => {
        // Utiliser le contenu actuel ou le template par défaut si customHtml est vide
        const finalContent = customHtml || getInitialTemplate();

        if (!finalContent) {
            toast.warning('Attention : Le contenu de l\'email est vide.');
            toast.info('Veuillez ajouter un contenu à l\'email.', { delay: 1000 });
            return;
        }
        setIsSending(true);
        try {
            // Le placeholder sera remplacé côté serveur par le vrai lien
            await onSendMail(email, finalContent);
            hideModal();
        } catch (error) {
            // Optionnel: afficher une erreur
        } finally {
            setIsSending(false);
        }
    };

    const handleCancelConfirm = () => {
        setIsConfirming(false);
    };

    return (
        <PortalModal
            id="send-mail-confirmation-modal"
            layout={false}
            size='xl'
            ref={ref}>
            <div className="bg-base-100 rounded-lg shadow-xl  mx-auto overflow-hidden">
                <div className="flex justify-between items-center bg-primary p-4 text-primary-content">
                    <p className="text-2xl font-bold">Envoyer le résumé par email</p>
                    <button
                        onClick={hideModal}
                        className="text-primary-content hover:text-base-300 hover:bg-base-100/20 rounded-full transition-all duration-200 focus:outline-none"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>
                <div className="p-6">
                    {!isConfirming ? (
                        <form onSubmit={handleSubmit}>
                            <div className="mb-6">
                                <label htmlFor="email" className="block text-sm font-medium text-base-content mb-2">
                                    Adresse email
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <EnvelopeIcon className="h-5 w-5 text-base-content/50" aria-hidden="true" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        id="email"
                                        className="focus:ring-accent focus:border-accent block w-full pl-10 pr-3 py-2 border border-base-300 rounded-md"
                                        placeholder="exemple@email.com"
                                        value={email}
                                        onChange={handleEmailChange}
                                        required
                                    />
                                </div>
                                <p className="mt-2 text-sm text-base-content/50">
                                    Le résumé des capsules sélectionnées sera envoyé à cette adresse.
                                </p>
                            </div>

                            <div className="mb-6 flex flex-col">
                                <label htmlFor="message-editor" className="block text-sm font-medium text-base-content mb-2">
                                    Contenu de l'email
                                </label>
                                <div className='flex flex-1 min-h-[350px] md:min-h-[550px]'>
                                    <BBCodeEditor
                                        initialValue={customHtml || getInitialTemplate()}
                                        onSave={setCustomHtml}
                                        debounceTime={0}
                                        containerHeight='auto'
                                        editorHeight='100%'
                                        id='message-editor'
                                    />
                                </div>
                            </div>
                            <div className="bg-primary border border-primary-content rounded-md p-3 space-y-2 flex flex-col flex-wrap">

                                <p className="text-sm text-primary-content font-medium mb-1">💡 Comment ajouter le bouton "Accéder à mon bilan" :</p>
                                <p className="text-xs text-primary-content">
                                    Si vous supprimez le bouton, tapez simplement à l'endroit souhaité :<br />
                                </p>
                                <span className="bg-base-100 text-base-content px-1 py-1 rounded text-xs w-fit whitespace-nowrap">

                                    <code className="">
                                        [LIEN_BILAN]
                                    </code>
                                </span>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 ">
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-outline"
                                    onClick={hideModal}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    Valider
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div>
                            <h2 className="text-lg font-semibold mb-4">Aperçu de l'email</h2>
                            <div className="border rounded p-4 bg-base-100 mb-6" style={{ overflowX: 'auto' }}>
                                <div dangerouslySetInnerHTML={{ __html: customHtml || getInitialTemplate() }} />
                            </div>
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={handleCancelConfirm}
                                    disabled={isSending}
                                >
                                    Retour
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary flex items-center justify-center"
                                    onClick={handleConfirmSend}
                                    disabled={isSending}
                                >
                                    {isSending && <span className="loading loading-spinner loading-xs mr-2" />}
                                    Confirmer l'envoi
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </PortalModal>
    );
});

export default SendMailConfirmationModal; 